import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { ordersAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Printer,
  Trash2,
  CheckSquare,
  Square,
  AlertTriangle,
  Check,
  Image,
  StickyNote,
  ClipboardList,
  Star,
  X,
  Save,
  Ban,
  MoreVertical,
  Clock,
  PackageX,
  Truck,
  Phone,
  Mail,
  CreditCard,
  Banknote,
  Plus,
  Search,
  PrinterCheck,
  DollarSign,
  Copy,
  RefreshCw,
  Hash,
  Tag,
  Download,
  TrendingUp,
  Package,
  Layers,
  FileText,
  Loader2,
  MapPin,
  ChevronDown,
  FolderOpen,
  ChevronRight,
  Folder,
} from "lucide-react";
import { perfumeAPI } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { openGlsLabel } from "@/components/GlsLabelDialog";

// Top seller codes and colors for picking list modal
const TOP_SELLER_CODES = ["1021", "8000", "M57", "2010", "ST10", "1076", "C0K", "222", "570", "VF7137"];
const ML_FORMATS = ["30ml", "50ml", "100ml", "200ml", "500ml"];

// All top seller codes use gray color
const CODE_COLORS = {
  "1021": { bg: "from-slate-600 to-slate-700", text: "text-slate-200", border: "border-slate-400/60", accent: "bg-slate-500" },
  "8000": { bg: "from-slate-600 to-slate-700", text: "text-slate-200", border: "border-slate-400/60", accent: "bg-slate-500" },
  "M57": { bg: "from-slate-600 to-slate-700", text: "text-slate-200", border: "border-slate-400/60", accent: "bg-slate-500" },
  "2010": { bg: "from-slate-600 to-slate-700", text: "text-slate-200", border: "border-slate-400/60", accent: "bg-slate-500" },
  "ST10": { bg: "from-slate-600 to-slate-700", text: "text-slate-200", border: "border-slate-400/60", accent: "bg-slate-500" },
  "1076": { bg: "from-slate-600 to-slate-700", text: "text-slate-200", border: "border-slate-400/60", accent: "bg-slate-500" },
  "C0K": { bg: "from-slate-600 to-slate-700", text: "text-slate-200", border: "border-slate-400/60", accent: "bg-slate-500" },
  "222": { bg: "from-slate-600 to-slate-700", text: "text-slate-200", border: "border-slate-400/60", accent: "bg-slate-500" },
  "570": { bg: "from-slate-600 to-slate-700", text: "text-slate-200", border: "border-slate-400/60", accent: "bg-slate-500" },
  "VF7137": { bg: "from-slate-600 to-slate-700", text: "text-slate-200", border: "border-slate-400/60", accent: "bg-slate-500" },
};

// Channel colors for quick identification
const CHANNEL_CONFIG = {
  whatsapp: { 
    label: "WhatsApp", 
    color: "bg-green-500 text-white",
    borderColor: "border-l-green-500"
  },
  tiktok_shop: { 
    label: "TikTok", 
    color: "bg-black text-white",
    borderColor: "border-l-black"
  },
  website: { 
    label: "Sito Web", 
    color: "bg-blue-500 text-white",
    borderColor: "border-l-blue-500"
  },
};

const STATUS_CONFIG = {
  pending: { label: "In attesa", color: "bg-gray-100 text-gray-700" },
  urgent: { label: "Urgente", color: "bg-red-100 text-red-700" },
  complete: { label: "Completo", color: "bg-green-100 text-green-700" },
  completed: { label: "Completato", color: "bg-green-100 text-green-700" },
  incomplete: { label: "Incompleto", color: "bg-orange-100 text-orange-700" },
  cancelled: { label: "Annullato", color: "bg-gray-300 text-gray-600" },
};

const COURIER_CONFIG = {
  GLS: { label: "GLS", color: "bg-yellow-500 text-white" },
  NAPOLI: { label: "Napoli", color: "bg-purple-500 text-white" },
};

const PAYMENT_CONFIG = {
  paid: { label: "Pagato", color: "bg-green-100 text-green-700", icon: CreditCard },
  cod: { label: "Contrassegno", color: "bg-orange-100 text-orange-700", icon: Banknote },
};

// Fixed prices per format and channel
const FIXED_PRICES = {
  "30ml": { whatsapp: 6, website: 6, tiktok_shop: 7 },
  "50ml": { whatsapp: 10, website: 10, tiktok_shop: 11 },
  "100ml": { whatsapp: 15, website: 15, tiktok_shop: 16 },
  "200ml": { whatsapp: 25, website: 25, tiktok_shop: 26 },
  "500ml": { whatsapp: 60, website: 60, tiktok_shop: 60 },
};

const FORMATS = ["30ml", "50ml", "100ml", "200ml", "500ml"];

const Sales = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [perfumes, setPerfumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [filterChannel, setFilterChannel] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCourier, setFilterCourier] = useState("all");
  const [filterPayment, setFilterPayment] = useState("all");
  const [filterPrinted, setFilterPrinted] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [loadingMore, setLoadingMore] = useState(false);
  const ITEMS_PER_PAGE = 50;
  
  // Modal states
  const [editingOrder, setEditingOrder] = useState(null);
  const sendToGlsQueue = async (ids) => {
    try {
      const r = await api.post("/gls/queue", { order_ids: ids });
      const { added, skipped } = r.data;
      toast.success(`${added} ${added === 1 ? "ordine inviato" : "ordini inviati"} a Etichette GLS${skipped ? ` · ${skipped} già in coda o generati` : ""}`, {
        action: { label: "Apri", onClick: () => navigate("/etichette-gls") },
      });
      setSelectedOrders([]);
      loadFolderOrders();
      loadOrders(page);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio a Etichette GLS");
    }
  };
  const [noteText, setNoteText] = useState("");
  
  // Incomplete modal state
  const [incompleteOrder, setIncompleteOrder] = useState(null);
  const [selectedMissingItems, setSelectedMissingItems] = useState([]);
  
  // Add items modal state
  const [addItemsOrder, setAddItemsOrder] = useState(null);
  const [addItemsSearch, setAddItemsSearch] = useState("");
  const [addItemsFormat, setAddItemsFormat] = useState("100ml");
  const [addItemsNewTotal, setAddItemsNewTotal] = useState("");
  const [pendingAddItems, setPendingAddItems] = useState([]);
  
  // Edit total modal state
  const [editTotalOrder, setEditTotalOrder] = useState(null);
  const [editTotalValue, setEditTotalValue] = useState("");
  
  // Picking List modal state
  const [pickingModalOpen, setPickingModalOpen] = useState(false);
  const [pickingLoading, setPickingLoading] = useState(false);
  const [pickingResult, setPickingResult] = useState(null);
  
  // Folders section state
  const [foldersOpen, setFoldersOpen] = useState(false);
  const [folderOrders, setFolderOrders] = useState({ GLS: [], NAPOLI: [], PERSONALIZZATO: [] });
  const [activeFolderView, setActiveFolderView] = useState(null); // Which folder is being viewed
  
  const fileInputRef = useRef(null);
  const [uploadingOrderId, setUploadingOrderId] = useState(null);

  useEffect(() => {
    loadOrders(1);
    loadPerfumes();
  }, []);

  const loadPerfumes = async () => {
    try {
      const res = await perfumeAPI.getAll();
      setPerfumes(res.data || []);
    } catch (error) {
      console.error("Error loading perfumes:", error);
    }
  };

  const loadOrders = async (pageNum, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      
      const res = await ordersAPI.getAll({ page: pageNum, limit: ITEMS_PER_PAGE });
      const data = res.data;
      
      if (append) {
        setOrders(prev => [...prev, ...data.orders]);
      } else {
        setOrders(data.orders || []);
      }
      
      setPagination(data.pagination || { total: 0, pages: 1 });
      setPage(pageNum);
    } catch (error) {
      toast.error("Errore caricamento ordini");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = () => {
    if (page < pagination.pages && !loadingMore) {
      loadOrders(page + 1, true);
    }
  };

  // Load folder orders on mount
  useEffect(() => {
    loadFolderOrders();
  }, []);

  // Load orders in folders
  const loadFolderOrders = async () => {
    try {
      const data = await ordersAPI.list();
      // Filter orders by folder, only non-completed
      const glsOrders = data.filter(o => o.folder === 'GLS' && o.status !== 'completed');
      const napoliOrders = data.filter(o => o.folder === 'NAPOLI' && o.status !== 'completed');
      const personalizzatoOrders = data.filter(o => o.folder === 'PERSONALIZZATO' && o.status !== 'completed');
      
      setFolderOrders({
        GLS: glsOrders,
        NAPOLI: napoliOrders,
        PERSONALIZZATO: personalizzatoOrders
      });
    } catch (error) {
      console.error("Error loading folder orders:", error);
    }
  };

  // Move selected orders to a folder
  const moveOrdersToFolder = async (folderName) => {
    if (selectedOrders.length === 0) {
      toast.error("Seleziona almeno un ordine");
      return;
    }
    
    try {
      // Update each selected order with the folder
      await Promise.all(
        selectedOrders.map(id => ordersAPI.update(id, { folder: folderName }))
      );
      
      toast.success(`${selectedOrders.length} ordini spostati in ${folderName}`);
      setSelectedOrders([]);
      loadFolderOrders();
      loadOrders(page);
    } catch (error) {
      toast.error("Errore nello spostamento ordini");
      console.error(error);
    }
  };

  // Remove order from folder (set folder to null)
  const removeFromFolder = async (orderId) => {
    try {
      await ordersAPI.update(orderId, { folder: null });
      toast.success("Ordine rimosso dalla cartella");
      loadFolderOrders();
    } catch (error) {
      toast.error("Errore nella rimozione");
    }
  };

  // Mark order as completed (will disappear from folder)
  const markOrderCompleted = async (orderId) => {
    try {
      await ordersAPI.update(orderId, { status: 'completed' });
      toast.success("Ordine completato");
      loadFolderOrders();
      loadOrders(page);
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  // Filter orders - memoized for performance
  const filteredOrders = useMemo(() => {
    let filtered = orders;
    
    if (filterChannel !== "all") {
      filtered = filtered.filter(o => o.channel === filterChannel);
    }
    
    if (filterStatus !== "all") {
      filtered = filtered.filter(o => (o.status || "pending") === filterStatus);
    }
    
    if (filterCourier !== "all") {
      filtered = filtered.filter(o => o.courier === filterCourier);
    }
    
    if (filterPayment !== "all") {
      filtered = filtered.filter(o => o.payment_method === filterPayment);
    }
    
    if (filterPrinted !== "all") {
      filtered = filtered.filter(o => filterPrinted === "printed" ? o.is_printed : !o.is_printed);
    }
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(o => {
        // Search by customer name
        if (o.customer_name?.toLowerCase().includes(query)) return true;
        // Search by customer address
        if (o.customer_address?.toLowerCase().includes(query)) return true;
        // Search by customer phone
        if (o.customer_phone?.includes(query)) return true;
        // Search by WooCommerce order number
        if (o.woo_order_id?.toString().includes(query)) return true;
        // Search by order ID
        if (o.id?.toLowerCase().includes(query)) return true;
        // Search by perfume code
        if (o.items?.some(item => item.perfume_code?.toLowerCase().includes(query))) return true;
        // Search by perfume name
        if (o.items?.some(item => item.perfume_name?.toLowerCase().includes(query))) return true;
        return false;
      });
    }
    
    return filtered;
  }, [orders, filterChannel, filterStatus, filterCourier, filterPayment, filterPrinted, searchQuery]);

  const handleSelectOrder = (orderId) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleSelectAll = () => {
    if (selectedOrders.length === filteredOrders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(filteredOrders.map(o => o.id));
    }
  };

  const handleUpdateStatus = async (orderId, status) => {
    try {
      // If changing to non-incomplete status, clear missing items
      const updateData = { status };
      if (status !== 'incomplete') {
        updateData.missing_items = [];
      }
      await ordersAPI.update(orderId, updateData);
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, ...updateData } : o));
      toast.success(`Stato: ${(STATUS_CONFIG[status] || STATUS_CONFIG.pending).label}`);
    } catch (error) {
      toast.error("Errore");
    }
  };

  const handleUpdateCourier = async (orderId, courier) => {
    try {
      await ordersAPI.update(orderId, { courier });
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, courier } : o));
      toast.success(`Corriere: ${COURIER_CONFIG[courier]?.label || courier}`);
    } catch (error) {
      toast.error("Errore");
    }
  };

  const handleUpdatePayment = async (orderId, payment_method) => {
    try {
      await ordersAPI.update(orderId, { payment_method });
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, payment_method } : o));
      toast.success(payment_method === 'paid' ? "✓ Segnato come Pagato" : "Segnato come Contrassegno");
    } catch (error) {
      toast.error("Errore aggiornamento pagamento");
    }
  };

  // Open incomplete modal to select missing items
  const handleOpenIncompleteModal = (order) => {
    setIncompleteOrder(order);
    setSelectedMissingItems(order.missing_items || []);
  };

  // Save incomplete status with missing items
  const handleSaveIncomplete = async () => {
    if (!incompleteOrder) return;
    try {
      await ordersAPI.update(incompleteOrder.id, { 
        status: 'incomplete', 
        missing_items: selectedMissingItems 
      });
      setOrders(prev => prev.map(o => 
        o.id === incompleteOrder.id 
          ? { ...o, status: 'incomplete', missing_items: selectedMissingItems } 
          : o
      ));
      setIncompleteOrder(null);
      setSelectedMissingItems([]);
      toast.success("Ordine incompleto salvato");
    } catch (error) {
      toast.error("Errore");
    }
  };

  // Toggle missing item selection
  const toggleMissingItem = (perfumeId) => {
    setSelectedMissingItems(prev => 
      prev.includes(perfumeId)
        ? prev.filter(id => id !== perfumeId)
        : [...prev, perfumeId]
    );
  };

  // Open add items modal
  const handleOpenAddItemsModal = (order) => {
    setAddItemsOrder(order);
    setAddItemsSearch("");
    setAddItemsFormat("100ml");
    setAddItemsNewTotal(order.total_amount.toFixed(2));
    setPendingAddItems([]);
  };

  // Get matching perfumes for add items search - exact match first
  const getMatchingPerfumesForAdd = () => {
    if (!addItemsSearch) return [];
    const searchLower = addItemsSearch.toLowerCase();
    return perfumes
      .filter(p => 
        p.code.toLowerCase().includes(searchLower) ||
        p.name.toLowerCase().includes(searchLower)
      )
      .sort((a, b) => {
        const aCode = a.code.toLowerCase();
        const bCode = b.code.toLowerCase();
        
        // Exact match has highest priority
        const aExact = aCode === searchLower;
        const bExact = bCode === searchLower;
        if (aExact && !bExact) return -1;
        if (bExact && !aExact) return 1;
        
        // Then startsWith has priority
        const aStarts = aCode.startsWith(searchLower);
        const bStarts = bCode.startsWith(searchLower);
        if (aStarts && !bStarts) return -1;
        if (bStarts && !aStarts) return 1;
        
        // Finally sort numerically
        const numA = parseInt(a.code.replace(/\D/g, '')) || 0;
        const numB = parseInt(b.code.replace(/\D/g, '')) || 0;
        if (numA !== numB) return numA - numB;
        return a.code.localeCompare(b.code);
      })
      .slice(0, 6);
  };

  // Add item to pending list
  const handleAddPendingItem = (perfume) => {
    const price = FIXED_PRICES[addItemsFormat]?.[addItemsOrder?.channel] || 0;
    setPendingAddItems(prev => [...prev, {
      perfume_id: perfume.id,
      perfume_code: perfume.code,
      perfume_name: perfume.name,
      format: addItemsFormat,
      quantity_ml: parseInt(addItemsFormat),
      unit_price: price
    }]);
    setAddItemsSearch("");
    
    // Auto-update total
    const newTotal = parseFloat(addItemsNewTotal || 0) + price;
    setAddItemsNewTotal(newTotal.toFixed(2));
  };

  // Remove pending item
  const handleRemovePendingItem = (index) => {
    const item = pendingAddItems[index];
    setPendingAddItems(prev => prev.filter((_, i) => i !== index));
    // Auto-update total
    const newTotal = parseFloat(addItemsNewTotal || 0) - item.unit_price;
    setAddItemsNewTotal(Math.max(0, newTotal).toFixed(2));
  };

  // Save add items
  const handleSaveAddItems = async () => {
    if (!addItemsOrder) return;
    try {
      const newItems = [...addItemsOrder.items, ...pendingAddItems];
      const newTotal = parseFloat(addItemsNewTotal) || addItemsOrder.total_amount;
      
      await ordersAPI.update(addItemsOrder.id, { 
        items: newItems,
        total_amount: newTotal
      });
      
      setOrders(prev => prev.map(o => 
        o.id === addItemsOrder.id 
          ? { ...o, items: newItems, total_amount: newTotal, total_items: newItems.length } 
          : o
      ));
      
      setAddItemsOrder(null);
      setPendingAddItems([]);
      toast.success(`Aggiunti ${pendingAddItems.length} articoli all'ordine`);
    } catch (error) {
      toast.error("Errore nell'aggiunta articoli");
    }
  };

  const handleToggleCustom = async (orderId) => {
    const order = orders.find(o => o.id === orderId);
    const newValue = !order.is_custom;
    try {
      await ordersAPI.update(orderId, { is_custom: newValue });
      setOrders(prev => prev.map(o => 
        o.id === orderId ? { ...o, is_custom: newValue } : o
      ));
      toast.success(newValue ? "Personalizzato" : "Standard");
    } catch (error) {
      toast.error("Errore");
    }
  };

  const handleSaveNote = async () => {
    if (!editingOrder) return;
    try {
      await ordersAPI.update(editingOrder.id, { notes: noteText });
      setOrders(prev => prev.map(o => 
        o.id === editingOrder.id ? { ...o, notes: noteText } : o
      ));
      setEditingOrder(null);
      toast.success("Nota salvata");
    } catch (error) {
      toast.error("Errore");
    }
  };

  // Handle edit total
  const handleOpenEditTotal = (order) => {
    setEditTotalOrder(order);
    setEditTotalValue(order.total_amount.toFixed(2));
  };

  const handleSaveTotal = async () => {
    if (!editTotalOrder) return;
    const newTotal = parseFloat(editTotalValue);
    if (isNaN(newTotal) || newTotal < 0) {
      toast.error("Importo non valido");
      return;
    }
    try {
      await ordersAPI.update(editTotalOrder.id, { total_amount: newTotal });
      setOrders(prev => prev.map(o => 
        o.id === editTotalOrder.id ? { ...o, total_amount: newTotal } : o
      ));
      setEditTotalOrder(null);
      toast.success("Importo aggiornato");
    } catch (error) {
      toast.error("Errore aggiornamento importo");
    }
  };

  const handleUploadImage = async (orderId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await ordersAPI.uploadImage(orderId, formData);
      setOrders(prev => prev.map(o => 
        o.id === orderId 
          ? { ...o, custom_image_url: res.data.image_url, is_custom: true } 
          : o
      ));
      toast.success("Immagine caricata");
    } catch (error) {
      toast.error("Errore upload");
    }
  };

  const handleRemoveImage = async (orderId) => {
    try {
      await ordersAPI.update(orderId, { custom_image_url: null, is_custom: false });
      setOrders(prev => prev.map(o => 
        o.id === orderId 
          ? { ...o, custom_image_url: null, is_custom: false } 
          : o
      ));
      toast.success("Grafica rimossa");
    } catch (error) {
      toast.error("Errore rimozione grafica");
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!confirm("Eliminare questo ordine?")) return;
    try {
      await ordersAPI.delete(orderId);
      setOrders(prev => prev.filter(o => o.id !== orderId));
      setSelectedOrders(prev => prev.filter(id => id !== orderId));
      toast.success("Eliminato");
    } catch (error) {
      toast.error("Errore");
    }
  };

  // Delete multiple selected orders
  const handleDeleteSelectedOrders = async () => {
    if (selectedOrders.length === 0) return;
    
    const confirmMessage = `Eliminare ${selectedOrders.length} ordini selezionati? Questa azione non può essere annullata.`;
    if (!confirm(confirmMessage)) return;
    
    try {
      let deleted = 0;
      let errors = 0;
      
      for (const orderId of selectedOrders) {
        try {
          await ordersAPI.delete(orderId);
          deleted++;
        } catch (e) {
          errors++;
        }
      }
      
      // Update local state
      setOrders(prev => prev.filter(o => !selectedOrders.includes(o.id)));
      setSelectedOrders([]);
      
      if (errors > 0) {
        toast.warning(`Eliminati ${deleted} ordini, ${errors} errori`);
      } else {
        toast.success(`${deleted} ordini eliminati`);
      }
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Complete multiple selected orders
  const handleCompleteSelectedOrders = async () => {
    if (selectedOrders.length === 0) return;
    
    const confirmMessage = `Segnare ${selectedOrders.length} ordini come completati?`;
    if (!confirm(confirmMessage)) return;
    
    try {
      let completed = 0;
      let errors = 0;
      
      for (const orderId of selectedOrders) {
        try {
          await ordersAPI.update(orderId, { status: 'complete' });
          completed++;
        } catch (e) {
          errors++;
        }
      }
      
      // Update local state
      setOrders(prev => prev.map(o => 
        selectedOrders.includes(o.id) ? { ...o, status: 'complete' } : o
      ));
      setSelectedOrders([]);
      
      if (errors > 0) {
        toast.warning(`Completati ${completed} ordini, ${errors} errori`);
      } else {
        toast.success(`${completed} ordini completati`);
      }
    } catch (error) {
      toast.error("Errore nel completamento");
    }
  };

  // Check and remove duplicate orders (same woo_order_id or same customer+items)
  const handleCheckDuplicates = async () => {
    setCheckingDuplicates(true);
    try {
      // Group orders by woo_order_id
      const wooOrderGroups = {};
      const manualOrderGroups = {};
      
      orders.forEach(order => {
        if (order.woo_order_id) {
          const key = order.woo_order_id.toString();
          if (!wooOrderGroups[key]) wooOrderGroups[key] = [];
          wooOrderGroups[key].push(order);
        } else {
          // For manual orders, group by customer name + items signature
          const itemsSignature = order.items
            ?.map(i => `${i.perfume_code}-${i.format}`)
            .sort()
            .join('|') || '';
          const key = `${order.customer_name || 'N/A'}_${itemsSignature}_${order.created_at?.substring(0, 10)}`;
          if (!manualOrderGroups[key]) manualOrderGroups[key] = [];
          manualOrderGroups[key].push(order);
        }
      });
      
      // Find duplicates (groups with more than 1 order)
      const duplicates = [];
      
      Object.entries(wooOrderGroups).forEach(([wooId, group]) => {
        if (group.length > 1) {
          // Keep the oldest, mark rest as duplicates
          const sorted = group.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          duplicates.push(...sorted.slice(1).map(o => ({ ...o, duplicateType: 'WooCommerce #' + wooId })));
        }
      });
      
      Object.entries(manualOrderGroups).forEach(([key, group]) => {
        if (group.length > 1) {
          const sorted = group.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          duplicates.push(...sorted.slice(1).map(o => ({ ...o, duplicateType: 'Stesso cliente/prodotti' })));
        }
      });
      
      if (duplicates.length === 0) {
        toast.success("✅ Nessun ordine duplicato trovato!");
        setCheckingDuplicates(false);
        return;
      }
      
      // Show confirmation with details
      const confirmMsg = `Trovati ${duplicates.length} ordini duplicati:\n\n` +
        duplicates.slice(0, 5).map(d => `• ${d.customer_name || 'N/A'} - ${d.duplicateType}`).join('\n') +
        (duplicates.length > 5 ? `\n...e altri ${duplicates.length - 5}` : '') +
        '\n\nVuoi eliminarli?';
      
      if (!confirm(confirmMsg)) {
        setCheckingDuplicates(false);
        return;
      }
      
      // Delete duplicates
      let deleted = 0;
      let errors = 0;
      
      for (const dup of duplicates) {
        try {
          await ordersAPI.delete(dup.id);
          deleted++;
        } catch (e) {
          errors++;
        }
      }
      
      // Update local state
      const deletedIds = duplicates.map(d => d.id);
      setOrders(prev => prev.filter(o => !deletedIds.includes(o.id)));
      
      if (errors > 0) {
        toast.warning(`Eliminati ${deleted} duplicati, ${errors} errori`);
      } else {
        toast.success(`✅ ${deleted} ordini duplicati eliminati!`);
      }
      
    } catch (error) {
      toast.error("Errore nel controllo duplicati");
      console.error(error);
    }
    setCheckingDuplicates(false);
  };

  // Generate PDF - SINGLE ORDER per page (etichetta 100x200mm) - Professional format with dynamic sizing
  const generatePDF = async (ordersToExport) => {
    const w = window.open('', '_blank');
    if (!w) {
      toast.error("Popup bloccato. Abilita i popup per stampare.");
      return;
    }
    w.document.write('<p style="font-family:sans-serif;padding:20px">Preparazione etichette...</p>');
    let labelCodes = {};
    try {
      labelCodes = (await api.post('/orders/label-codes', { order_ids: ordersToExport.map(o => o.id) })).data;
    } catch {
      toast.error("Codici a barre non disponibili: riprova la stampa");
      w.close();
      return;
    }
    setOrders(prev => prev.map(o => (labelCodes[o.id] ? { ...o, label_code: labelCodes[o.id] } : o)));

    const printContent = ordersToExport.map((order) => {
      // Permanent barcode saved on the order, so scans always find it
      const barcodeValue = labelCodes[order.id] || '';
      const orderNumber = order.channel === 'website' && order.woo_order_id
        ? `ORDINE #${order.woo_order_id}`
        : (barcodeValue ? `ORDINE N° ${barcodeValue.slice(-4)}` : '');
      
      // Group items by code and format
      const itemsGrouped = {};
      order.items.forEach(item => {
        const key = `${item.perfume_code}-${item.format}`;
        if (!itemsGrouped[key]) {
          itemsGrouped[key] = { code: item.perfume_code, format: item.format, qty: 0 };
        }
        itemsGrouped[key].qty += item.quantity || 1;
      });
      
      const itemsList = Object.values(itemsGrouped)
        .sort((a, b) => (parseInt(a.code) || 0) - (parseInt(b.code) || 0));
      
      const itemCount = itemsList.length;
      
      // Items auto-fit: the print script picks columns and font size so everything stays on one page
      const itemsHtml = `<div class="items">${itemsList.map(item => `
            <div class="item">
              <span><span class="code">${item.code}</span> <span class="fmt">${item.format}</span></span>
              ${item.qty > 1 ? `<span class="qty">x${item.qty}</span>` : ''}
            </div>`).join('')}
          </div>`;

      // Total text - Special handling for WEBSITE orders (both GLS and NAPOLI)
      let totalText;
      const shippingCost = order.shipping_total || 0;
      const netAmount = (order.total_amount || 0) - shippingCost;
      
      if (order.channel === 'website') {
        // Website orders: show PAGATO if paid, or net amount + SPED if COD
        if (order.payment_method === 'paid') {
          totalText = 'PAGATO';
        } else {
          // COD (contrassegno) - show net amount without shipping
          totalText = `€${netAmount.toFixed(2)} <span style="font-weight:900;color:#8B5CF6;">+ SPED</span>`;
        }
      } else if (order.channel === 'whatsapp' && order.payment_method === 'paid') {
        totalText = 'PAGATO';
      } else if (order.channel === 'whatsapp') {
        totalText = `€${order.total_amount.toFixed(2)} + SPED.`;
      } else {
        totalText = `€${order.total_amount.toFixed(2)}`;
      }

      // Channel and courier badges
      const channelColor = order.channel === 'whatsapp' ? '#22C55E' : order.channel === 'tiktok_shop' ? '#000' : '#3B82F6';
      const channelLabel = order.channel === 'website' ? 'SITO WEB' : order.channel === 'whatsapp' ? 'WHATSAPP' : 'TIKTOK';
      const courierColor = order.courier === 'GLS' ? '#EAB308' : order.courier === 'NAPOLI' ? '#8B5CF6' : '#666';

      // Clean address
      const cleanAddress = order.customer_address 
        ? order.customer_address.replace(/\s+/g, ' ').trim()
        : '';

      return `
        <div class="label-page">
          <!-- Header: Channel + Courier -->
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;">
            <span style="background:${channelColor};color:white;padding:6px 14px;border-radius:6px;font-size:14px;font-weight:800;letter-spacing:0.5px;">${channelLabel}</span>
            ${(orderNumber || barcodeValue) ? `<div style="text-align:center;line-height:1.05;">
              ${orderNumber ? `<div style="font-size:21px;font-weight:900;color:#000;white-space:nowrap;">${orderNumber}</div>` : ''}
              ${barcodeValue ? `<div style="font-size:15px;font-weight:800;color:#000;font-family:monospace;letter-spacing:1px;white-space:nowrap;">${barcodeValue}</div>` : ''}
            </div>` : ''}
            <span style="background:${courierColor};color:white;padding:6px 14px;border-radius:6px;font-size:14px;font-weight:800;">${order.courier || 'N/A'}</span>
          </div>
          
          <!-- Customer Name - LARGE, CENTERED, BOLD -->
          <h1 style="font-size:28px;font-weight:900;margin:8px 0 12px 0;color:#000;text-align:center;text-transform:uppercase;letter-spacing:1px;line-height:1.2;">
            ${order.customer_name || 'N/A'}
          </h1>
          
          <!-- Address - LARGER for courier -->
          ${cleanAddress ? `
            <div style="background:#E8F4FD;border-left:5px solid #3B82F6;padding:12px 14px;margin-bottom:10px;border-radius:0 8px 8px 0;">
              <p style="font-size:18px;font-weight:700;color:#1E3A5F;margin:0;line-height:1.4;">📍 ${cleanAddress}</p>
            </div>
          ` : ''}
          
          <!-- Phone - LARGER and Visible -->
          ${order.customer_phone ? `
            <div style="background:#E8F8E8;border-left:5px solid #22C55E;padding:10px 14px;margin-bottom:12px;border-radius:0 8px 8px 0;">
              <p style="font-size:22px;font-weight:800;color:#166534;margin:0;">📞 ${order.customer_phone}</p>
            </div>
          ` : ''}
          
          <!-- Items Section - auto-fit -->
          <div class="items-box" data-count="${itemCount}">
            ${itemsHtml}
          </div>
          
          <!-- Notes if present -->
          ${order.notes ? `
            <div style="background:#FEF3C7;border:2px solid #F59E0B;padding:8px 12px;border-radius:6px;margin:8px 0;">
              <p style="font-size:14px;font-weight:700;color:#92400E;margin:0;">📝 ${order.notes}</p>
            </div>
          ` : ''}
          
          <!-- Total - LARGE at bottom -->
          <div style="padding-top:12px;border-top:4px solid #000;text-align:center;">
            <span style="font-size:32px;font-weight:900;color:#000;">${totalText}</span>
          </div>
          
          <!-- Barcode for NAPOLI and GLS orders - compact at very bottom -->
          ${barcodeValue ? `
            <div style="margin-top:8px;text-align:center;padding-top:6px;border-top:1px dashed #999;">
              <svg id="barcode-${order.id}" class="barcode-svg" data-code="${barcodeValue}"></svg>
              <div style="font-size:14px;font-weight:800;color:#000;margin-top:2px;font-family:monospace;letter-spacing:1px;">${barcodeValue}</div>
            </div>
          ` : ''}
        </div>
      `;
    }).join('');

    const html = `<!DOCTYPE html><html><head><title>Etichette Ordini</title>
      <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"></script>
      <style>
        @page { 
          margin: 0 !important; 
          padding: 0 !important;
          size: 100mm 200mm;
        }
        * { 
          box-sizing: border-box; 
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          margin: 0;
          padding: 0;
        }
        html, body { 
          font-family: Arial, Helvetica, sans-serif; 
          margin: 0 !important; 
          padding: 0 !important;
          width: 100mm;
        }
        .label-page {
          width: 100mm;
          height: 200mm;
          padding: 3mm;
          page-break-after: always;
          break-after: page;
          page-break-inside: avoid;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .label-page > * { flex-shrink: 0; }
        .items-box {
          flex: 1 1 auto !important;
          min-height: 0;
          margin: 6px 0;
          border-top: 3px solid #000;
          border-bottom: 3px solid #000;
          padding: 6px 0;
          overflow: hidden;
        }
        .items {
          --cols: 1;
          --fs: 30px;
          display: grid;
          grid-template-columns: repeat(var(--cols), minmax(0, 1fr));
          gap: calc(var(--fs) * 0.2);
          font-size: var(--fs);
          line-height: 1.15;
        }
        .item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 4px;
          background: #f5f5f5;
          padding: calc(var(--fs) * 0.22) calc(var(--fs) * 0.35);
          border-radius: 4px;
          min-width: 0;
        }
        .item .code { font-weight: 900; color: #000; }
        .item .fmt { font-size: 0.65em; font-weight: 700; color: #333; }
        .item .qty { font-weight: 900; color: #B8860B; white-space: nowrap; }
        .label-page:last-child {
          page-break-after: auto;
        }
        .barcode-svg {
          max-width: 80mm;
          height: 18mm;
          display: block;
          margin: 0 auto;
        }
        @media print { 
          @page {
            margin: 0 !important;
            padding: 0 !important;
          }
          html, body {
            width: 100mm;
            margin: 0 !important;
            padding: 0 !important;
          }
          .label-page { 
            page-break-inside: avoid;
            break-inside: avoid;
            margin: 0 !important;
          }
        }
      </style>
      </head><body>${printContent}
      <script>
        // Generate barcodes after page loads
        document.querySelectorAll('.barcode-svg').forEach(svg => {
          const code = svg.getAttribute('data-code');
          if (code && typeof JsBarcode !== 'undefined') {
            JsBarcode(svg, code, {
              format: "CODE128",
              width: 2,
              height: 45,
              displayValue: false,
              margin: 0
            });
          }
        });
        // Fit items: biggest font first, adding columns before shrinking further
        const fits = (box) => box.scrollHeight <= box.clientHeight + 1;
        document.querySelectorAll('.items-box').forEach(box => {
          const items = box.querySelector('.items');
          const n = Number(box.getAttribute('data-count')) || 1;
          const maxCols = Math.min(4, n);
          for (let fs = 30; fs >= 7; fs -= 1) {
            let ok = false;
            for (let cols = 1; cols <= maxCols; cols++) {
              items.style.setProperty('--cols', cols);
              items.style.setProperty('--fs', fs + 'px');
              if (fits(box)) { ok = true; break; }
            }
            if (ok) break;
          }
        });
      </script>
      </body></html>`;

    w.document.open();
    w.document.write(html);
    w.document.close();
    w.onload = () => {
      w.print();
    };
    markOrdersAsPrinted(ordersToExport.map(o => o.id));
  };

  // Mark orders as printed
  const markOrdersAsPrinted = async (orderIds) => {
    if (!orderIds || orderIds.length === 0) return;
    
    try {
      // Update each order as printed - use Promise.allSettled to handle partial failures
      const results = await Promise.allSettled(
        orderIds.map(id => ordersAPI.update(id, { is_printed: true }))
      );
      
      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const failCount = results.filter(r => r.status === 'rejected').length;
      
      // Update local state for successful updates
      setOrders(prev => prev.map(o => 
        orderIds.includes(o.id) ? { ...o, is_printed: true } : o
      ));
      
      if (failCount > 0) {
        toast.warning(`${successCount} ordini segnati come stampati, ${failCount} errori`);
      } else {
        toast.success(`${successCount} ordini segnati come stampati`);
      }
    } catch (error) {
      console.error("Error marking orders as printed:", error);
      toast.error("Errore nel segnare gli ordini come stampati");
    }
  };

  // Generate Picking List - Grouped by perfume code with formats inline
  const generatePickingList = async (ordersToExport) => {
    if (ordersToExport.length === 0) {
      toast.error("Seleziona almeno un ordine");
      return;
    }
    
    setPickingModalOpen(true);
    setPickingLoading(true);
    setPickingResult(null);
    
    try {
      const orderIds = ordersToExport.map(o => o.id);
      const res = await api.post("/picking/from-orders", { order_ids: orderIds });
      
      if (res.data && res.data.success) {
        setPickingResult(res.data);
        toast.success(res.data.message);
      } else {
        toast.error(res.data?.detail || "Errore nella generazione");
        setPickingModalOpen(false);
      }
    } catch (error) {
      console.error("Picking list error:", error);
      toast.error(error.response?.data?.detail || "Errore nella generazione della picking list");
      setPickingModalOpen(false);
    } finally {
      setPickingLoading(false);
    }
  };
  
  const downloadPickingPDF = useCallback(() => {
    if (!pickingResult?.pdf) return;
    
    const byteCharacters = atob(pickingResult.pdf.data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/pdf" });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = pickingResult.pdf.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success(`Download: ${pickingResult.pdf.filename}`);
  }, [pickingResult]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#C9A227] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="sales-page" className="animate-fadeIn">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl md:text-3xl font-serif text-gray-900">Archivio Vendite</h1>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleCheckDuplicates}
            disabled={checkingDuplicates}
            className="bg-orange-50 border-orange-300 text-orange-700 hover:bg-orange-100"
            data-testid="btn-check-duplicates"
          >
            {checkingDuplicates ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Copy className="w-4 h-4 mr-2" />
            )}
            Controllo Duplicati
          </Button>
        </div>

        {/* Folders Section - Collapsible */}
        <div className="mb-6">
          <button
            onClick={() => setFoldersOpen(!foldersOpen)}
            className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-slate-100 to-slate-50 hover:from-slate-200 hover:to-slate-100 rounded-xl border border-slate-200 transition-all"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-slate-700 rounded-lg flex items-center justify-center">
                <FolderOpen className="w-4 h-4 text-white" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-800">Cartelle Ordini</p>
                <p className="text-xs text-slate-500">
                  {folderOrders.GLS.length + folderOrders.NAPOLI.length + folderOrders.PERSONALIZZATO.length} ordini organizzati
                </p>
              </div>
            </div>
            <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${foldersOpen ? 'rotate-90' : ''}`} />
          </button>
          
          {foldersOpen && (
            <div className="mt-3 space-y-3">
              {/* Folder Cards */}
              <div className="grid grid-cols-3 gap-4">
                {/* GLS Folder */}
                <button
                  className={`flex flex-col items-center p-5 bg-white rounded-xl border-2 transition-all group ${
                    activeFolderView === 'GLS' ? 'border-amber-400 bg-amber-50' : 'border-slate-200 hover:border-amber-300 hover:bg-amber-50'
                  }`}
                  onClick={() => setActiveFolderView(activeFolderView === 'GLS' ? null : 'GLS')}
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-amber-500 rounded-2xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform shadow-lg">
                    <Folder className="w-7 h-7 text-white" />
                  </div>
                  <span className="font-bold text-slate-700">GLS</span>
                  <span className="text-lg font-black text-amber-600">{folderOrders.GLS.length}</span>
                  <span className="text-xs text-slate-400">ordini</span>
                </button>
                
                {/* NAPOLI Folder */}
                <button
                  className={`flex flex-col items-center p-5 bg-white rounded-xl border-2 transition-all group ${
                    activeFolderView === 'NAPOLI' ? 'border-violet-400 bg-violet-50' : 'border-slate-200 hover:border-violet-300 hover:bg-violet-50'
                  }`}
                  onClick={() => setActiveFolderView(activeFolderView === 'NAPOLI' ? null : 'NAPOLI')}
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-violet-400 to-violet-500 rounded-2xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform shadow-lg">
                    <Folder className="w-7 h-7 text-white" />
                  </div>
                  <span className="font-bold text-slate-700">NAPOLI</span>
                  <span className="text-lg font-black text-violet-600">{folderOrders.NAPOLI.length}</span>
                  <span className="text-xs text-slate-400">ordini</span>
                </button>
                
                {/* PERSONALIZZATO Folder */}
                <button
                  className={`flex flex-col items-center p-5 bg-white rounded-xl border-2 transition-all group ${
                    activeFolderView === 'PERSONALIZZATO' ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:border-emerald-300 hover:bg-emerald-50'
                  }`}
                  onClick={() => setActiveFolderView(activeFolderView === 'PERSONALIZZATO' ? null : 'PERSONALIZZATO')}
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-2xl flex items-center justify-center mb-2 group-hover:scale-110 transition-transform shadow-lg">
                    <Folder className="w-7 h-7 text-white" />
                  </div>
                  <span className="font-bold text-slate-700">PERSONALIZZATO</span>
                  <span className="text-lg font-black text-emerald-600">{folderOrders.PERSONALIZZATO.length}</span>
                  <span className="text-xs text-slate-400">ordini</span>
                </button>
              </div>
              
              {/* Folder Contents */}
              {activeFolderView && folderOrders[activeFolderView] && (
                <div className={`p-4 rounded-xl border-2 ${
                  activeFolderView === 'GLS' ? 'bg-amber-50 border-amber-200' :
                  activeFolderView === 'NAPOLI' ? 'bg-violet-50 border-violet-200' :
                  'bg-emerald-50 border-emerald-200'
                }`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-slate-800">Ordini in {activeFolderView}</h4>
                    <span className="text-sm text-slate-500">{folderOrders[activeFolderView].length} ordini</span>
                  </div>
                  
                  {folderOrders[activeFolderView].length === 0 ? (
                    <p className="text-center text-slate-500 py-4">Nessun ordine in questa cartella</p>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {folderOrders[activeFolderView].map(order => (
                        <div key={order.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                          <div>
                            <p className="font-semibold text-slate-800">{order.customer_name || 'N/A'}</p>
                            <p className="text-xs text-slate-500">
                              {order.items?.length || 0} prodotti • €{order.total_amount?.toFixed(2)}
                              {order.is_printed && <span className="ml-2 text-green-600">✓ Stampato</span>}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => removeFromFolder(order.id)}
                              className="text-slate-500 hover:text-red-500"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => markOrderCompleted(order.id)}
                              className="bg-green-500 hover:bg-green-600 text-white"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Search bar */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Cerca per nome, telefono, n° ordine, codice profumo..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10"
            data-testid="search-input"
          />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <Select value={filterChannel} onValueChange={setFilterChannel}>
            <SelectTrigger className="w-[140px]" data-testid="filter-channel">
              <SelectValue placeholder="Canale" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i canali</SelectItem>
              <SelectItem value="whatsapp">WhatsApp</SelectItem>
              <SelectItem value="tiktok_shop">TikTok</SelectItem>
              <SelectItem value="website">Sito Web</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px]" data-testid="filter-status">
              <SelectValue placeholder="Stato" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="pending">In attesa</SelectItem>
              <SelectItem value="complete">Completo</SelectItem>
              <SelectItem value="incomplete">Incompleto</SelectItem>
              <SelectItem value="urgent">Urgente</SelectItem>
              <SelectItem value="cancelled">Annullato</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterCourier} onValueChange={setFilterCourier}>
            <SelectTrigger className="w-[120px]" data-testid="filter-courier">
              <SelectValue placeholder="Corriere" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="GLS">GLS</SelectItem>
              <SelectItem value="NAPOLI">Napoli</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterPayment} onValueChange={setFilterPayment}>
            <SelectTrigger className="w-[140px]" data-testid="filter-payment">
              <SelectValue placeholder="Pagamento" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="paid">Pagato</SelectItem>
              <SelectItem value="cod">Contrassegno</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterPrinted} onValueChange={setFilterPrinted}>
            <SelectTrigger className="w-[140px]" data-testid="filter-printed">
              <SelectValue placeholder="Stampa" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="printed">Stampati</SelectItem>
              <SelectItem value="not_printed">Non stampati</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex-1" />
          
          {selectedOrders.length > 0 && (
            <>
              <span className="text-sm font-medium text-[#C9A227]">{selectedOrders.length} selezionati</span>
              
              {/* Dropdown Sposta in Cartella */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" className="bg-slate-50 border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="btn-move-to-folder">
                    <FolderOpen className="w-4 h-4 mr-1" /> Sposta in
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => moveOrdersToFolder('GLS')} data-testid="move-to-gls">
                    <div className="w-3 h-3 rounded-full bg-amber-400 mr-2" />
                    <span>GLS</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => moveOrdersToFolder('NAPOLI')} data-testid="move-to-napoli">
                    <div className="w-3 h-3 rounded-full bg-violet-500 mr-2" />
                    <span>NAPOLI</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => moveOrdersToFolder('PERSONALIZZATO')} data-testid="move-to-personalizzato">
                    <div className="w-3 h-3 rounded-full bg-emerald-500 mr-2" />
                    <span>PERSONALIZZATO</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => moveOrdersToFolder(null)} className="text-slate-500" data-testid="remove-from-folder">
                    <X className="w-3 h-3 mr-2" />
                    <span>Rimuovi dalla cartella</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Button size="sm" className="bg-yellow-500 hover:bg-yellow-600 text-white" onClick={() => sendToGlsQueue(selectedOrders)} data-testid="btn-send-gls">
                <Truck className="w-4 h-4 mr-1" /> Spedizione GLS
              </Button>
              <Button size="sm" variant="outline" className="bg-green-50 border-green-300 text-green-700 hover:bg-green-100" onClick={handleCompleteSelectedOrders} data-testid="btn-complete-selected">
                <Check className="w-4 h-4 mr-1" /> Completa
              </Button>
              <Button size="sm" variant="outline" onClick={() => generatePDF(orders.filter(o => selectedOrders.includes(o.id)))} data-testid="btn-print">
                <Printer className="w-4 h-4 mr-1" /> Stampa
              </Button>
              <Button size="sm" variant="outline" onClick={() => generatePickingList(orders.filter(o => selectedOrders.includes(o.id)))} data-testid="btn-picking">
                <ClipboardList className="w-4 h-4 mr-1" /> Picking
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                className="bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-100"
                onClick={() => {
                  // Navigate to label print page with selected orders
                  window.location.href = `/stampa-etichette?orders=${selectedOrders.join(',')}`;
                }} 
                data-testid="btn-labels"
              >
                <Tag className="w-4 h-4 mr-1" /> Etichette
              </Button>
              <Button size="sm" variant="destructive" onClick={handleDeleteSelectedOrders} data-testid="btn-delete-selected">
                <Trash2 className="w-4 h-4 mr-1" /> Elimina
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Select all */}
      {filteredOrders.length > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg mb-3">
          <button onClick={handleSelectAll} className="text-gray-500 hover:text-[#C9A227]" data-testid="btn-select-all">
            {selectedOrders.length === filteredOrders.length && filteredOrders.length > 0 
              ? <CheckSquare className="w-5 h-5" /> 
              : <Square className="w-5 h-5" />}
          </button>
          <span className="text-sm text-gray-500">Seleziona tutti ({filteredOrders.length})</span>
        </div>
      )}

      {/* Orders list */}
      <div className="space-y-3">
        {filteredOrders.length === 0 ? (
          <div className="text-center py-12 text-gray-400">Nessun ordine</div>
        ) : (
          filteredOrders.map(order => {
            const channelConfig = CHANNEL_CONFIG[order.channel] || CHANNEL_CONFIG.website;
            const isComplete = order.status === 'complete';
            const isIncomplete = order.status === 'incomplete';
            const missingItems = order.missing_items || [];
            
            return (
              <div 
                key={order.id} 
                data-testid={`order-${order.id}`}
                className={`
                  rounded-xl border-l-4 transition-all
                  ${channelConfig.borderColor}
                  ${order.shipped
                    ? 'bg-sky-100 border border-sky-300'
                    : isComplete 
                    ? 'bg-green-50 border border-green-200' 
                    : isIncomplete
                      ? 'bg-orange-50 border border-orange-200'
                      : order.is_custom 
                        ? 'bg-amber-50 border border-amber-200'
                        : 'bg-white border border-gray-200'
                  }
                  ${selectedOrders.includes(order.id) ? 'ring-2 ring-[#C9A227]' : ''}
                  hover:shadow-md
                `}
              >
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Checkbox */}
                    <button onClick={() => handleSelectOrder(order.id)} className="mt-1" data-testid={`checkbox-${order.id}`}>
                      {selectedOrders.includes(order.id) 
                        ? <CheckSquare className="w-5 h-5 text-[#C9A227]" /> 
                        : <Square className="w-5 h-5 text-gray-400" />}
                    </button>
                    
                    {/* Order content */}
                    <div className="flex-1 min-w-0">
                      {/* Header row */}
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="font-semibold text-gray-900">{order.customer_name || 'N/A'}</span>
                        <Badge className={channelConfig.color}>
                          {channelConfig.label}
                        </Badge>
                        <Badge className={(STATUS_CONFIG[order.status] || STATUS_CONFIG.pending).color}>
                          {(STATUS_CONFIG[order.status] || STATUS_CONFIG.pending).label}
                        </Badge>
                        {order.courier && COURIER_CONFIG[order.courier] && (
                          <Badge className={COURIER_CONFIG[order.courier].color}>
                            {COURIER_CONFIG[order.courier].label}
                          </Badge>
                        )}
                        {/* Payment method - clickable to change for whatsapp/website orders */}
                        {(order.channel === 'whatsapp' || order.channel === 'website') ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Badge 
                                className={`cursor-pointer hover:opacity-80 ${
                                  order.payment_method === 'paid' 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-orange-100 text-orange-700'
                                }`}
                              >
                                {order.payment_method === 'paid' ? (
                                  <CreditCard className="w-3 h-3 mr-1" />
                                ) : (
                                  <Banknote className="w-3 h-3 mr-1" />
                                )}
                                {order.payment_method === 'paid' ? 'Pagato' : 'Contrassegno'}
                                <ChevronDown className="w-3 h-3 ml-1" />
                              </Badge>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuItem 
                                onClick={() => handleUpdatePayment(order.id, 'paid')}
                                className={order.payment_method === 'paid' ? 'bg-green-50' : ''}
                              >
                                <CreditCard className="w-4 h-4 mr-2 text-green-600" />
                                <span>Pagato</span>
                                {order.payment_method === 'paid' && <Check className="w-4 h-4 ml-auto" />}
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleUpdatePayment(order.id, 'cod')}
                                className={order.payment_method === 'cod' ? 'bg-orange-50' : ''}
                              >
                                <Banknote className="w-4 h-4 mr-2 text-orange-600" />
                                <span>Contrassegno</span>
                                {order.payment_method === 'cod' && <Check className="w-4 h-4 ml-auto" />}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : order.payment_method && PAYMENT_CONFIG[order.payment_method] && (
                          <Badge className={PAYMENT_CONFIG[order.payment_method].color}>
                            {order.payment_method === 'paid' ? (
                              <CreditCard className="w-3 h-3 mr-1" />
                            ) : (
                              <Banknote className="w-3 h-3 mr-1" />
                            )}
                            {PAYMENT_CONFIG[order.payment_method].label}
                          </Badge>
                        )}
                        {order.is_custom && (
                          <Badge className="bg-amber-200 text-amber-800">
                            <Star className="w-3 h-3 mr-1" /> Personalizzato
                          </Badge>
                        )}
                        {order.is_printed && (
                          <Badge className="bg-blue-100 text-blue-700">
                            <PrinterCheck className="w-3 h-3 mr-1" /> Stampato
                          </Badge>
                        )}
                        {order.shipped && (
                          <Badge className="bg-sky-500 text-white" data-testid={`shipped-badge-${order.id}`}>
                            <Truck className="w-3 h-3 mr-1" /> INVIATA
                          </Badge>
                        )}
                      </div>
                      
                      {/* Address */}
                      {order.customer_address && (
                        <p className="text-sm text-gray-500 mb-1">{order.customer_address}</p>
                      )}
                      
                      {/* Phone and Email */}
                      {(order.customer_phone || order.customer_email) && (
                        <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-2">
                          {order.customer_phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {order.customer_phone}
                            </span>
                          )}
                          {order.customer_email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" /> {order.customer_email}
                            </span>
                          )}
                        </div>
                      )}
                      
                      {/* Items - with missing items highlighted in red */}
                      <div className="flex flex-wrap gap-1 mb-2">
                        {order.items.map((item, i) => {
                          const isMissing = missingItems.includes(item.perfume_id);
                          return (
                            <span 
                              key={i} 
                              className={`px-2 py-0.5 rounded text-sm ${
                                isMissing 
                                  ? 'bg-red-100 text-red-700 font-medium border border-red-300' 
                                  : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {item.perfume_code} - {item.format}
                              {isMissing && <span className="ml-1 text-xs">(mancante)</span>}
                            </span>
                          );
                        })}
                      </div>
                      
                      {/* Note */}
                      {order.notes && (
                        <div className="bg-yellow-50 px-3 py-2 rounded-lg mb-2 text-sm italic text-gray-700">
                          📝 {order.notes}
                        </div>
                      )}
                      
                      {/* Custom Image */}
                      {order.custom_image_url && (
                        <div className="mb-2">
                          <img 
                            src={order.custom_image_url} 
                            alt="Grafica personalizzata" 
                            className="max-h-24 rounded-lg border-2 border-amber-300"
                          />
                        </div>
                      )}
                      
                      {/* Price and DateTime */}
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-[#C9A227]">
                          €{order.total_amount.toFixed(2)}
                          {order.channel === 'whatsapp' && <span className="text-gray-500 font-normal text-sm ml-1">+ SPEED</span>}
                        </span>
                        <div className="flex items-center gap-2">
                          {order.gls_tracking && (
                            <button
                              onClick={() => openGlsLabel(order.id)}
                              className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded flex items-center gap-1 hover:bg-yellow-200"
                              data-testid={`gls-tracking-${order.id}`}
                            >
                              <Truck className="w-3 h-3" />{order.gls_tracking}
                            </button>
                          )}
                          {order.woo_order_id && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded flex items-center gap-1">
                              <Hash className="w-3 h-3" />{order.woo_order_id}
                            </span>
                          )}
                          <span className="text-sm text-gray-500">
                            {new Date(order.created_at).toLocaleDateString('it-IT')} {new Date(order.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Dropdown menu */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`menu-${order.id}`}>
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        {/* Status options */}
                        <DropdownMenuItem 
                          onClick={() => handleUpdateStatus(order.id, 'pending')}
                          className="text-gray-600"
                          data-testid={`menu-pending-${order.id}`}
                        >
                          <Clock className="w-4 h-4 mr-2" /> In attesa
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleUpdateStatus(order.id, 'complete')}
                          className="text-green-600"
                          data-testid={`menu-complete-${order.id}`}
                        >
                          <Check className="w-4 h-4 mr-2" /> Completo
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleOpenIncompleteModal(order)}
                          className="text-orange-600"
                          data-testid={`menu-incomplete-${order.id}`}
                        >
                          <PackageX className="w-4 h-4 mr-2" /> Incompleto...
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleUpdateStatus(order.id, 'urgent')}
                          className="text-red-600"
                          data-testid={`menu-urgent-${order.id}`}
                        >
                          <AlertTriangle className="w-4 h-4 mr-2" /> Urgente
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleUpdateStatus(order.id, 'cancelled')}
                          className="text-gray-500"
                          data-testid={`menu-cancelled-${order.id}`}
                        >
                          <Ban className="w-4 h-4 mr-2" /> Annullato
                        </DropdownMenuItem>
                        
                        <DropdownMenuSeparator />
                        
                        {!order.gls_tracking && (
                          <DropdownMenuItem
                            onClick={() => sendToGlsQueue([order.id])}
                            className="text-yellow-700"
                            data-testid={`menu-gls-label-${order.id}`}
                          >
                            <Truck className="w-4 h-4 mr-2" /> Spedizione GLS
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem 
                          onClick={() => handleToggleCustom(order.id)}
                          className="text-amber-600"
                          data-testid={`menu-custom-${order.id}`}
                        >
                          <Star className="w-4 h-4 mr-2" /> 
                          {order.is_custom ? 'Rimuovi personalizzato' : 'Personalizzato'}
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => { setUploadingOrderId(order.id); fileInputRef.current?.click(); }}
                          data-testid={`menu-upload-${order.id}`}
                        >
                          <Image className="w-4 h-4 mr-2" /> 
                          {order.custom_image_url ? 'Cambia grafica' : 'Aggiungi grafica'}
                        </DropdownMenuItem>
                        {order.custom_image_url && (
                          <DropdownMenuItem 
                            onClick={() => handleRemoveImage(order.id)}
                            className="text-red-600"
                            data-testid={`menu-remove-image-${order.id}`}
                          >
                            <X className="w-4 h-4 mr-2" /> Rimuovi grafica
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem 
                          onClick={() => { setEditingOrder(order); setNoteText(order.notes || ''); }}
                          data-testid={`menu-note-${order.id}`}
                        >
                          <StickyNote className="w-4 h-4 mr-2" /> Aggiungi nota
                        </DropdownMenuItem>
                        
                        <DropdownMenuItem 
                          onClick={() => handleOpenAddItemsModal(order)}
                          className="text-blue-600"
                          data-testid={`menu-add-items-${order.id}`}
                        >
                          <Plus className="w-4 h-4 mr-2" /> Aggiunte
                        </DropdownMenuItem>
                        
                        <DropdownMenuItem 
                          onClick={() => handleOpenEditTotal(order)}
                          className="text-green-600"
                          data-testid={`menu-edit-total-${order.id}`}
                        >
                          <DollarSign className="w-4 h-4 mr-2" /> Modifica Importo
                        </DropdownMenuItem>
                        
                        <DropdownMenuSeparator />
                        
                        {/* Courier options */}
                        <DropdownMenuItem 
                          onClick={() => handleUpdateCourier(order.id, 'GLS')}
                          className={order.courier === 'GLS' ? 'text-yellow-600 bg-yellow-50' : 'text-yellow-600'}
                          data-testid={`menu-gls-${order.id}`}
                        >
                          <Truck className="w-4 h-4 mr-2" /> GLS {order.courier === 'GLS' && '✓'}
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleUpdateCourier(order.id, 'NAPOLI')}
                          className={order.courier === 'NAPOLI' ? 'text-purple-600 bg-purple-50' : 'text-purple-600'}
                          data-testid={`menu-napoli-${order.id}`}
                        >
                          <Truck className="w-4 h-4 mr-2" /> Napoli {order.courier === 'NAPOLI' && '✓'}
                        </DropdownMenuItem>
                        
                        <DropdownMenuSeparator />
                        
                        <DropdownMenuItem 
                          onClick={() => handleDeleteOrder(order.id)}
                          className="text-red-600"
                          data-testid={`menu-delete-${order.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> Elimina
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Load More Button */}
      {page < pagination.pages && (
        <div className="flex justify-center py-6">
          <Button 
            onClick={loadMore} 
            disabled={loadingMore}
            variant="outline"
            className="px-8"
            data-testid="load-more-btn"
          >
            {loadingMore ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                Caricamento...
              </span>
            ) : (
              `Carica altri (${pagination.total - orders.length} rimanenti)`
            )}
          </Button>
        </div>
      )}

      {/* Pagination Info */}
      <div className="text-center text-sm text-gray-500 pb-4">
        Mostrati {orders.length} di {pagination.total} ordini
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.[0] && uploadingOrderId) {
            handleUploadImage(uploadingOrderId, e.target.files[0]);
          }
          e.target.value = '';
        }}
      />

      {/* Note Modal */}
      {editingOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-[9999] pt-20 px-4 overflow-y-auto">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl mb-20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Nota per {editingOrder.customer_name || 'ordine'}</h3>
              <button onClick={() => setEditingOrder(null)}><X className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
            </div>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Scrivi una nota..."
              rows={4}
              autoFocus
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#C9A227] resize-none"
              data-testid="note-textarea"
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setEditingOrder(null)}>Annulla</Button>
              <Button onClick={handleSaveNote} className="bg-[#C9A227] hover:bg-[#D4AF37]" data-testid="btn-save-note">
                <Save className="w-4 h-4 mr-2" /> Salva
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Incomplete Modal - Select missing items */}
      {incompleteOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-[9999] pt-20 px-4 overflow-y-auto">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl mb-20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Profumi mancanti</h3>
              <button onClick={() => { setIncompleteOrder(null); setSelectedMissingItems([]); }}>
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            
            <p className="text-sm text-gray-500 mb-4">
              Seleziona i profumi mancanti per l'ordine di <strong>{incompleteOrder.customer_name || 'N/A'}</strong>
            </p>
            
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {incompleteOrder.items.map((item, i) => {
                const isSelected = selectedMissingItems.includes(item.perfume_id);
                return (
                  <button
                    key={i}
                    onClick={() => toggleMissingItem(item.perfume_id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                      isSelected 
                        ? 'bg-red-50 border-red-300 text-red-700' 
                        : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                    }`}
                    data-testid={`missing-item-${item.perfume_id}`}
                  >
                    {isSelected 
                      ? <CheckSquare className="w-5 h-5 text-red-500" /> 
                      : <Square className="w-5 h-5 text-gray-400" />}
                    <span className="font-medium">{item.perfume_code}</span>
                    <span className="text-sm opacity-75">{item.perfume_name}</span>
                    <span className="text-sm opacity-75">- {item.format}</span>
                  </button>
                );
              })}
            </div>
            
            {selectedMissingItems.length > 0 && (
              <div className="mt-4 p-3 bg-red-50 rounded-lg text-sm text-red-700">
                <strong>{selectedMissingItems.length}</strong> profum{selectedMissingItems.length === 1 ? 'o' : 'i'} mancant{selectedMissingItems.length === 1 ? 'e' : 'i'} - 
                verr{selectedMissingItems.length === 1 ? 'à' : 'anno'} evidenziat{selectedMissingItems.length === 1 ? 'o' : 'i'} in rosso
              </div>
            )}
            
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => { setIncompleteOrder(null); setSelectedMissingItems([]); }}>
                Annulla
              </Button>
              <Button 
                onClick={handleSaveIncomplete} 
                className="bg-orange-500 hover:bg-orange-600"
                data-testid="btn-save-incomplete"
              >
                <PackageX className="w-4 h-4 mr-2" /> Segna come incompleto
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Items Modal */}
      {addItemsOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-[9999] pt-20 px-4 overflow-y-auto">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-2xl mb-20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Aggiunte all'ordine</h3>
              <button onClick={() => { setAddItemsOrder(null); setPendingAddItems([]); }}>
                <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
              </button>
            </div>
            
            <p className="text-sm text-gray-500 mb-4">
              Ordine di <strong>{addItemsOrder.customer_name || 'N/A'}</strong> - {addItemsOrder.items.length} articoli attuali
            </p>
            
            {/* Search perfume */}
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Cerca profumo</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={addItemsSearch}
                    onChange={(e) => setAddItemsSearch(e.target.value)}
                    placeholder="Codice o nome..."
                    className="pl-10"
                    data-testid="add-items-search"
                  />
                </div>
                <select
                  value={addItemsFormat}
                  onChange={(e) => setAddItemsFormat(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  data-testid="add-items-format"
                >
                  {FORMATS.map(f => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </div>
              
              {/* Search results */}
              {addItemsSearch && (
                <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                  {getMatchingPerfumesForAdd().map(p => (
                    <button
                      key={p.id}
                      onClick={() => handleAddPendingItem(p)}
                      className="w-full p-2 text-left bg-gray-50 hover:bg-green-50 rounded-lg border border-gray-100 hover:border-green-300 transition-colors flex items-center justify-between"
                    >
                      <span>
                        <span className="text-[#C9A227] font-bold mr-2">{p.code}</span>
                        <span className="text-gray-600 text-sm">{p.clean_name || p.name}</span>
                      </span>
                      <span className="text-green-600 text-sm font-medium">
                        €{FIXED_PRICES[addItemsFormat]?.[addItemsOrder.channel] || 0}
                      </span>
                    </button>
                  ))}
                  {getMatchingPerfumesForAdd().length === 0 && (
                    <p className="text-gray-400 text-center py-2 text-sm">Nessun risultato</p>
                  )}
                </div>
              )}
            </div>
            
            {/* Pending items to add */}
            {pendingAddItems.length > 0 && (
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Articoli da aggiungere ({pendingAddItems.length})
                </label>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {pendingAddItems.map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-green-50 rounded-lg border border-green-200">
                      <span>
                        <span className="text-[#C9A227] font-bold mr-2">{item.perfume_code}</span>
                        <span className="text-gray-600 text-sm">{item.format}</span>
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-green-600 font-medium">€{item.unit_price}</span>
                        <button
                          onClick={() => handleRemovePendingItem(i)}
                          className="p-1 text-red-400 hover:text-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* New total */}
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Nuovo totale ordine (€)</label>
              <Input
                type="number"
                step="0.01"
                value={addItemsNewTotal}
                onChange={(e) => setAddItemsNewTotal(e.target.value)}
                className="text-lg font-bold"
                data-testid="add-items-total"
              />
              <p className="text-xs text-gray-400 mt-1">
                Totale originale: €{addItemsOrder.total_amount.toFixed(2)}
              </p>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setAddItemsOrder(null); setPendingAddItems([]); }}>
                Annulla
              </Button>
              <Button 
                onClick={handleSaveAddItems} 
                disabled={pendingAddItems.length === 0}
                className="bg-blue-500 hover:bg-blue-600"
                data-testid="btn-save-add-items"
              >
                <Plus className="w-4 h-4 mr-2" /> Salva Aggiunte
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Total Modal */}
      {editTotalOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] px-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Modifica Importo</h3>
              <button onClick={() => setEditTotalOrder(null)}>
                <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
              </button>
            </div>
            
            <p className="text-sm text-gray-500 mb-4">
              Ordine di <strong>{editTotalOrder.customer_name || 'N/A'}</strong>
            </p>
            
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Nuovo importo (€)</label>
              <input
                type="number"
                step="0.01"
                value={editTotalValue}
                onChange={(e) => setEditTotalValue(e.target.value)}
                className="w-full px-4 py-3 text-2xl font-bold text-center border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                data-testid="edit-total-input"
                autoFocus
              />
              <p className="text-xs text-gray-400 mt-2 text-center">
                Importo precedente: €{editTotalOrder.total_amount.toFixed(2)}
              </p>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditTotalOrder(null)}>
                Annulla
              </Button>
              <Button 
                onClick={handleSaveTotal}
                className="bg-green-500 hover:bg-green-600"
                data-testid="btn-save-total"
              >
                <Save className="w-4 h-4 mr-2" /> Salva
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Picking List Modal */}
      {pickingModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="picking-modal">
          <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden border border-gray-700/50 shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-700/50">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-[#C9A227]/30 to-[#C9A227]/10 border border-[#C9A227]/30">
                  <ClipboardList className="w-6 h-6 text-[#C9A227]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Picking List Unificata</h2>
                  <p className="text-sm text-gray-400">Ordini selezionati elaborati</p>
                </div>
              </div>
              <button 
                onClick={() => { setPickingModalOpen(false); setPickingResult(null); }}
                className="p-2 hover:bg-gray-700/50 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400 hover:text-white" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {pickingLoading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <Loader2 className="w-12 h-12 text-[#C9A227] animate-spin mb-4" />
                  <p className="text-white font-medium">Generazione picking list...</p>
                </div>
              ) : pickingResult ? (
                <div className="space-y-6">
                  {/* Stats Row */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="rounded-xl bg-gray-800/50 border border-gray-700/50 p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-[#C9A227]/20">
                          <FileText className="w-5 h-5 text-[#C9A227]" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-white">{pickingResult.orders_processed}</p>
                          <p className="text-xs text-gray-400">Ordini</p>
                        </div>
                      </div>
                    </div>
                    <div className="rounded-xl bg-gray-800/50 border border-gray-700/50 p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-[#C9A227]/20">
                          <Package className="w-5 h-5 text-[#C9A227]" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-white">{pickingResult.total_products}</p>
                          <p className="text-xs text-gray-400">Prodotti</p>
                        </div>
                      </div>
                    </div>
                    <div className="rounded-xl bg-gray-800/50 border border-gray-700/50 p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-[#C9A227]/20">
                          <Layers className="w-5 h-5 text-[#C9A227]" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-white">{pickingResult.total_quantity}</p>
                          <p className="text-xs text-gray-400">Quantità</p>
                        </div>
                      </div>
                    </div>
                    <div className="rounded-xl bg-emerald-900/30 border border-emerald-500/30 p-4">
                      <Button
                        onClick={downloadPickingPDF}
                        className="w-full h-full bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="picking-download-btn"
                      >
                        <Download className="w-5 h-5 mr-2" />
                        Scarica PDF
                      </Button>
                    </div>
                  </div>
                  
                  {/* Top Sellers Grid */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-[#C9A227]" />
                      Codici Top Seller
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                      {TOP_SELLER_CODES.map((code) => {
                        const data = pickingResult.top_sellers?.[code] || { total: 0 };
                        const colors = CODE_COLORS[code] || CODE_COLORS["8000"];
                        return (
                          <div 
                            key={code}
                            className={`relative rounded-xl border-2 ${colors.border} bg-gradient-to-br ${colors.bg} p-4`}
                          >
                            <div className={`absolute top-0 left-0 right-0 h-1 ${colors.accent}`} />
                            <div className="flex items-center justify-between mb-3">
                              <span className={`font-mono font-bold text-lg ${colors.text}`}>{code}</span>
                              {data.total > 0 && (
                                <span className={`text-sm font-bold px-2 py-0.5 rounded bg-black/30 ${colors.text}`}>
                                  {data.total}
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-5 gap-1 text-center">
                              {ML_FORMATS.map((format) => (
                                <div key={format} className="text-xs">
                                  <p className="text-gray-500">{format.replace('ml', '')}</p>
                                  <p className={`font-bold ${data[format] > 0 ? 'text-white' : 'text-gray-600'}`}>
                                    {data[format] || "-"}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* All Products Table */}
                  {pickingResult.all_products && Object.keys(pickingResult.all_products).length > 0 && (
                    <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 overflow-hidden">
                      <div className="p-4 border-b border-gray-700/50">
                        <h3 className="text-white font-semibold flex items-center gap-2">
                          <Package className="w-5 h-5 text-[#C9A227]" />
                          Tutti i Prodotti ({Object.keys(pickingResult.all_products).length})
                        </h3>
                      </div>
                      <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                        <table className="w-full">
                          <thead className="sticky top-0 bg-gray-800">
                            <tr className="text-left">
                              <th className="p-3 text-gray-400 font-medium text-sm">Codice</th>
                              <th className="p-3 text-gray-400 font-medium text-sm text-center">30ml</th>
                              <th className="p-3 text-gray-400 font-medium text-sm text-center">50ml</th>
                              <th className="p-3 text-gray-400 font-medium text-sm text-center">100ml</th>
                              <th className="p-3 text-gray-400 font-medium text-sm text-center">200ml</th>
                              <th className="p-3 text-gray-400 font-medium text-sm text-center">500ml</th>
                              <th className="p-3 text-gray-400 font-medium text-sm text-center">Totale</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(pickingResult.all_products)
                              .sort((a, b) => {
                                const totalA = Object.values(a[1]).reduce((sum, v) => sum + v, 0);
                                const totalB = Object.values(b[1]).reduce((sum, v) => sum + v, 0);
                                return totalB - totalA;
                              })
                              .map(([code, formats]) => {
                                const total = Object.values(formats).reduce((sum, v) => sum + v, 0);
                                const isTopSeller = TOP_SELLER_CODES.includes(code);
                                return (
                                  <tr key={code} className={`border-t border-gray-700/30 hover:bg-gray-700/30 ${isTopSeller ? 'bg-slate-700/30' : ''}`}>
                                    <td className="p-3">
                                      <span className={`font-mono font-semibold ${isTopSeller ? 'text-slate-300' : 'text-white'}`}>
                                        {code}
                                      </span>
                                    </td>
                                    <td className="p-3 text-center text-gray-300">{formats["30ml"] || "-"}</td>
                                    <td className="p-3 text-center text-gray-300">{formats["50ml"] || "-"}</td>
                                    <td className="p-3 text-center text-gray-300">{formats["100ml"] || "-"}</td>
                                    <td className="p-3 text-center text-gray-300">{formats["200ml"] || "-"}</td>
                                    <td className="p-3 text-center text-gray-300">{formats["500ml"] || "-"}</td>
                                    <td className="p-3 text-center font-bold text-white">{total}</td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Sales;
